import RateCap from "./utils/RateCap.js";
import WeakIterable from "./utils/WeakIterable.js";
import OpenLayersDriver from "./view/OpenLayersDriver.js";
import D3Driver from "./view/D3Driver.js";
import global from "./global.js";
import {GIS_TYPE_GOOGLE_MAPS, GIS_TYPE_OSM, GIS_TYPE_ESRI_2D} from "./view/OpenLayersDriver";
const d3 = global.d3;

const log = global.log.getLogger("f_mtac.view");
log.setLevel(log.levels.WARN);

const parse_px = (px) => parseFloat(px.replace("px", ""));

const compute_padding = (container) => {
    log.debug("View.compute_padding ", {container});
    var {paddingTop: top, paddingBottom: bottom, paddingLeft: left, paddingRight: right} = window.getComputedStyle(container, null);
    top = parse_px(top);
    bottom = parse_px(bottom);
    left = parse_px(left);
    right = parse_px(right);
    return {top, bottom, left, right};
};

const compute_width = (container) => {
    log.debug("View.compute_width ", {container});
    var {width, height} = window.getComputedStyle(container, null);
    width = parse_px(width);
    height = parse_px(height);
    return {width, height};
};

const compute_margin = ({top, left, right}) => {
    log.debug("View.compute_margin ", {top, left, right});
    return {
        top: top.offsetHeight,
        bottom: top.offsetHeight / 9, // assuming top is 9/8*Wb
        left: left.offsetWidth,
        right: right.offsetWidth
    };
};

/**
 * This class is responsible for the properties of the view:
 *  * zoom and translation for D3
 *  * center and resolution for OpenLayers.
 * - It uses D3Driver to handle the properties on D3 elements (SVG drawings)
 * - It uses OpenLayersDriver to handle the properties on OpenLayers (Google map)
 */
const View = function (container_selector, command_selectors) {
    log.debug("View ctor");
    var context = null;
    var layers = new WeakIterable();
    var locked_position = null;
    var zoom_active = false;
    var transform_state = {}; // state of the D3 display (x, y, k)
    var gis_state = {}; // state of the OpenLayers display (center, zoom)
    var d3Driver = new D3Driver(this, container_selector);
    var olDriver = {};
    var is_gis_authorized = false;

    // Define properties
    // -----------------

    this.container = window.document.querySelector(container_selector);
    this.commands = {
        top: window.document.querySelector(command_selectors.top),
        left: window.document.querySelector(command_selectors.left),
        right: window.document.querySelector(command_selectors.right)
    };

    Object.assign(this, compute_width(this.container));

    this.margin = compute_margin(this.commands);
    this.padding = compute_padding(this.container);
    this.refresh = function () {
        Object.assign(this, compute_width(this.container));
    };

    this.refresh();

    Object.defineProperty(this, "ol_driver", {
        set: () => {},
        get: () => olDriver
    });

    Object.defineProperty(this, "d3_driver", {
        set: () => {},
        get: () => d3Driver
    });

    Object.defineProperty(this, "locked_position", {
        set: () => {},
        get: () => locked_position
    });

    Object.defineProperty(this, "zoom_active", {
        set: (value) => {
            zoom_active = value;
        },
        get: () => zoom_active
    });

    Object.defineProperty(this, "is_gis", {
        set: () => {},
        get: () => {
            if (global.scene === undefined) {
                return false;
            }
            //from now, is_gis = gis type defined (googlemaps, osm,...) and network is online and gis key api not null
            if (is_gis_authorized) {
                return global.server.network.state === "online" ? global.scene.map.is_gis : false;
            } else {
                return false;
            }
        }
    });

    Object.defineProperty(this, "is_gis_authorized", {
        set: () => {},
        get: () => {
            return is_gis_authorized; // in configuration file
        }
    });

    Object.defineProperty(this, "gis_state", {
        set: () => {},
        get: () => gis_state
    });

    // check if here is a GIS context (with center, resolution and map type)
    Object.defineProperty(this, "has_gis_context", {
        set: () => {},
        get: () => {
            try {
                let data = JSON.parse(JSON.stringify(context.data));
                log.debug("has_gis_context context data = ", {data});
                // check center... could be resolution
                if (context.data.gis.center !== undefined) {
                    log.debug("has_gis_context = true");
                    return true;
                }
            } catch (error) {
                log.debug("has_gis_context error", {error});
            }
            log.debug("has_gis_context = false");
            return false;
        }
    });

    // check if here is a media context (with transformation x, y k)
    Object.defineProperty(this, "has_media_context", {
        set: () => {},
        get: () => {
            if (context === null) {
                log.debug("has_media_context = false");
                return false;
            }
            if (context.data.transform === undefined) {
                log.debug("has_media_context = false");
                return false;
            }
            log.debug("has_media_context = true");
            return true;
        }
    });

    // Methods common to D3 and OpenLayers projections
    // -----------------------------------------------

    this.layer_add = function (layer) {
        log.debug("View.layer_add ", {layer});
        layers.add(layer);
    };

    this.layer_remove = function (layer) {
        log.debug("View.layer_remove ", {layer});
        layers.delete(layer);
    };

    /**
     * Apply the current context:
     * - center, resolution and map type for GIS
     * - transform for D3
     * @returns {boolean} true if context found and applied
     */
    this.apply_context = () => {
        // id map is a GIS
        if (this.has_gis_context && this.is_gis) {
            let data = JSON.parse(JSON.stringify(context.data));
            log.debug("View.apply_context context data = ", {data});
            olDriver.set_center_and_resolution(data.gis.center, data.gis.resolution);
            // initial frame values restored after context change
            if (global.scene.mode_switcher.mode === "map") {
                olDriver.set_geocoding_address(data.gis.geocoding);
            }
            olDriver.set_initial_zoom_and_tiles(data.gis.zoom, data.gis.tiles);

            // change resource scale, depending on the zoom value
            olDriver.update_resource_scale();

            global.scene.map.map_gis.set_fallback_media(data.gis.fallback_media_id);
            return true;
        }

        // if map is a picture
        if (this.has_media_context && !this.is_gis) {
            let data = JSON.parse(JSON.stringify(context.data));
            log.debug("View.apply_context context data = ", {data});
            let x = context.data.transform.x;
            let y = context.data.transform.y;
            let k = context.data.transform.k;
            d3Driver.transform_to({x, y, k});

            // resource scale is the one of the menu
            global.resources.scale = global.resources.scale_setting;

            return true;
        }
        return false;
    };

    /**
     * Apply the transformation saved in the context
     * @param new_context
     */
    this.change_context = async (new_context) => {
        var old_context = context;
        if (old_context !== null) {
            let old_data = JSON.parse(JSON.stringify(context.data));
            log.debug("View.change_context old context data = ", {old_data});
            await old_context.data_commit();
        }
        context = new_context;
        // Add log
        if (context != null) {
            let data = JSON.parse(JSON.stringify(context.data));
            log.debug("View.change_context new context data = ", {data});
        } else {
            log.debug("View.new context = null");
        }
    };

    /**
     * Commit the context data in IndexedDB after some millisec
     * @type {RateCap}
     */
    let commit_rate_cap = new RateCap(() => {
        if (context === null) {
            return;
        }
        context.data_commit();
    }, 500); // 2 Hz

    /**
     * Apply transformation after some millisec
     * @type {RateCap}
     */
    let transform_rate_cap = new RateCap(() => {
        for (let layer of layers) {
            layer.transform(transform_state);
        }
        if (context === null) {
            return;
        }
        log.debug("transform_rate_cap", {transform_state});

        if (context.data.transform === undefined) {
            context.data.transform = {};
        }
        context.data.transform.x = transform_state.x;
        context.data.transform.y = transform_state.y;
        context.data.transform.k = transform_state.k;

        log.debug("View.transform_rate_cap", {gis_state});
        if (this.is_gis) {
            log.debug("transform_rate_cap", {gis_state});
            // set only if gis, because checked in property has_gis_context
            if (context.data.gis === undefined) {
                context.data.gis = {};
            }
            context.data.gis.center = gis_state.center; // [int,int]
            context.data.gis.resolution = gis_state.resolution;
            context.data.gis.map_basic_layer_type = olDriver.mapBasicLayerType;
        }
    }, 50); // 20 Hz (bad)

    /**
     * Apply an x, y translation to the D3 elements
     * @param x
     * @param y
     */
    this.translate = (x, y) => {
        log.debug("View.translate", {x, y});
        if (!this.is_gis) {
            d3Driver.translate(x, y);
        }
    };

    this.transform_apply = () => {
        transform_rate_cap.fire();
    };

    /**
     * Do a resize and its the computations
     */
    this.resize = () => {
        window.dispatchEvent(new Event("resize"));
    };

    /**
     * zoom: called when the zoom button is pressed
     * @param dk float: zoom rate of the map
     */
    this.zoom_by = (dk) => {
        log.debug("View.zoom_by", {dk});

        if (this.is_gis) {
            if (dk == 1) {
                // called when resize window
                return;
            }
            olDriver.zoom(dk);
        } else {
            d3Driver.zoom(dk);
            // when gis and offline, save the new resolution
            if (this.is_gis_authorized) {
                olDriver.compute_gis_offline();
            }
        }
    };

    /**
     * Save the position (bounding box) of an SVG object.
     * Center the view to the object
     * @param position Bounding box of the object
     */
    this.lock_position = function (position) {
        log.debug("View.lock_position", {position});
        this.unlock_position();
        locked_position = position;
        if (zoom_active === false) {
            if (this.is_gis) {
                this.ol_driver.center_to_object(locked_position);
            } else {
                if (context !== null) {
                    this.d3_driver.center_to_object(locked_position, context.data.transform.k);
                }
            }
        }
    };

    /**
     * Reset the locked position
     */
    this.unlock_position = function () {
        log.debug("View.unlock_position");
        locked_position = null;
    };

    /**
     * Center the view to the locked position
     */
    this.center_to_locked_position = function () {
        if (locked_position !== null) {
            log.debug("View.center_to_locked_position", {locked_position});
            if (this.is_gis) {
                this.ol_driver.center_to_object(locked_position);
            } else {
                if (context !== null) {
                    this.d3_driver.center_to_object(locked_position, context.data.transform.k);
                }
            }
        }
    };

    /**
     * This method is called by OpenlayerDriver
     * Apply given transformation {x, y, k} immediately
     * Save the corresponding center and resolution
     * @param x
     * @param y
     * @param k
     * @param center
     * @param resolution
     */
    this.launch_transform = function (x, y, k, center, resolution) {
        transform_state = {x, y, k};
        if (center !== undefined) {
            // launch_transform may be called without center and resolution
            gis_state = {center, resolution};
        }
        transform_rate_cap.launch();
        commit_rate_cap.launch();
    };

    /**
     * Set  view type (media or gis)
     * Activate de-activate elements, depending on the type
     * @param type
     */
    this.set_view_type = function (type) {
        log.debug("View.set_view_type", {type});
        let canvas = document.getElementsByTagName("canvas");
        if (type == "gis") {
            global.svg.root.node().style.pointerEvents = "none";
            canvas[0].style.pointerEvents = "none";
        } else {
            // not sure it is worth disabling pointer-events
            global.svg.root.node().style.pointerEvents = "";
            canvas[0].style.pointerEvents = "";
        }
        // active zoom handler for all maps in order to manage map move when tracking is activated
        d3Driver.zoom_active_handlers_enable();
    };

    /**
     * Switch map type (ex: ROADMAP, SATELLITE, TERRAIN for Google map)
     * and save it in the context data
     */
    this.switch_map_basic_layer = function (fromAssistant) {
        log.debug("View.switch_map_type");
        olDriver.switch_map_basic_layer(fromAssistant);
        if (context != null && context.data.gis !== undefined) {
            global.scene.map.map_gis.update_gis_layer_type(olDriver.mapBasicLayerType);
            global.view.save_map_type_gis_context(olDriver.mapBasicLayerType);
        } else {
            log.warn("switch_map_type: can't save context");
        }
    };

    // ---------------------------
    // Methods for D3 only
    // ---------------------------

    /**
     * Handle zoom event on D3 HTML element
     * @param state_ {x, y, k} object
     */
    this.on_d3_zoom = (state_) => {
        if (!this.is_gis) {
            transform_state = state_;
            transform_rate_cap.poll();
            commit_rate_cap.poll();
        }
    };

    /**
     * Handle resize event from D3 event system
     */
    d3.select(window).on("resize", () => {
        Object.assign(this.margin, compute_margin(this.commands));
        this.refresh();
        for (let layer of layers) {
            layer.resize();
        }
        d3Driver.zoom(1); // trigger reflow
    });

    /**
     * Enable zooming on D3 HTML elements
     */
    this.enable_zoom = () => {
        log.debug("View.enable_zoom");
        if (!this.is_gis) {
            d3Driver.enable_zoom();
        }
    };

    /**
     * Enable zooming on D3 HTML elements
     */
    this.disable_zoom = () => {
        log.debug("View.disable_zoom");
        if (!this.is_gis) {
            d3Driver.disable_zoom();
        }
    };

    /**
     * @brief compute the inbound window size available for centering something
     * @param {*} m additional margin
     * @return a tuple with the width, the height and the margins
     */
    this.compute_inbound_window = (m) => {
        var {width: OW, height: OH} = this; // outer width / height
        var {left: L, right: R, top: T, bottom: B} = this.margin; // view margin
        if (m === undefined) {
            m = global.app.scale.wb / 8; // additional margin
        }
        let left = L + m;
        let right = R + m;
        let top = T;
        let bottom = B;

        // Window where to display the box, takeing into account the margin
        let width = OW - left - right,
            height = OH - top - bottom; // inner width / height
        log.debug("View.compute_inbound_window", {width, height, left, right, top, bottom});
        return {width, height, left, right, top, bottom};
    };

    /**
     * Center a given, box in the view
     * @param w box width
     * @param h box height
     * @param m margin
     * @param ox horizontal position of the left side
     * @param oy vertival position of the top side
     */
    this.d3_center_to_box = ({width: w, height: h, margin: m, ox, oy}) => {
        let iw = this.compute_inbound_window(m);

        // k is the ratio between the window and the box
        var k = Math.min(iw.width / w, iw.height / h);

        // x, y is the translation of the top-left corner
        var x = iw.left + (iw.width - w * k) / 2;
        var y = iw.top + (iw.height - h * k) / 2;

        if (ox !== undefined && oy !== undefined) {
            // if the image was rotated, take into account the top origin of the bounding box
            x -= ox * k;
            y -= oy * k;
        }
        log.debug("View.d3_center_to_box", {x, y, k});

        d3Driver.transform_to({x, y, k});
    };

    /**
     * Center a given box in the view
     * @param width box width
     * @param height box height
     * @param margin margin
     */
    this.d3_center_to_box_2 = ({width, height, margin}) => {
        let {x, y, k} = this.get_centering_parameters({width, height, margin});
        d3Driver.transform_to({x, y, k});
    };

    /**
     * Get the centering parameters of a given box
     * @param w box width
     * @param h box height
     * @param m margin
     */
    this.get_centering_parameters = ({width, height, margin}) => {
        log.debug("View.get_centering_parameters in", {width, height, margin});
        var {width: view_width, height: view_height} = this; // view width / height
        var {left: L, right: R, top: T, bottom: B} = this.margin; // view margin
        if (margin === undefined) {
            margin = 0; // global.app.scale.wb / 4; // additional margin
        }
        L += margin;
        R += margin;
        T += margin;
        B += margin;
        var IW = view_width - L - R,
            IH = view_height - T - B; // inner width / height

        var k = Math.min(IW / width, IH / height);
        var x = L + (IW - width * k) / 2;
        var y = T + (IH - height * k) / 2;

        log.debug("View.get_centering_parameters out", {x, y, k});

        return {x, y, k};
    };

    // Methods for OpenLayers only
    // ---------------------------

    this.hide_svg = function () {
        global.svg.root.node().style.display = "none";
    };

    this.show_svg = function () {
        global.svg.root.node().style.display = "";
    };

    this.save_gis_context = function (state) {
        log.debug("View.save_gis_context", {state});
        context.data.type = "gis";
        context.data.gis = {};
        context.data.gis.center = state.viewState.center;
        context.data.gis.resolution = state.viewState.resolution;
        context.data.gis.map_basic_layer_type = olDriver.mapBasicLayerType;

        // save zoom and tiles in context when creating view
        context.data.gis.zoom = state.zoom;
        context.data.gis.tiles = state.tiles;
        context.data_commit();
    };

    // save media cache in context
    this.save_map_gis_context = function (mediaId, state) {
        log.debug("View.save_map_gis_context", {mediaId});
        context.data.gis.fallback_media_id = mediaId;

        // save zoom and tiles in context when creating view from shared mission
        if (state !== undefined) {
            context.data.gis.zoom = state.zoom;
            context.data.gis.tiles = state.tiles;
        }
        context.data_commit();
    };

    // save map type in context
    this.save_map_type_gis_context = function (type) {
        context.data.gis.map_basic_layer_type = type;
        context.data_commit();
    };

    this.save_geocoding_gis_context = function (address) {
        log.debug("View.save_geocoding_gis_context", {address});
        // gis context not yet created if geocoding address called in Assistant map creation
        if (context.data.gis === undefined) {
            return;
        }
        context.data.gis.geocoding = address;
        context.data_commit();
    };

    this.resize = () => {
        log.debug("View.resize");
        window.dispatchEvent(new Event("resize"));
    };

    if (
        global.default_configuration.gis.type == undefined ||
        ![GIS_TYPE_GOOGLE_MAPS, GIS_TYPE_OSM, GIS_TYPE_ESRI_2D].includes(global.default_configuration.gis.type) ||
        (global.default_configuration.gis.type === GIS_TYPE_GOOGLE_MAPS && global.default_configuration.gis.api_key == "")
    ) {
        $("#media-and-gis").hide();
        is_gis_authorized = false;
    } else {
        olDriver = new OpenLayersDriver(this, global.default_configuration.gis.type);
        olDriver.activate();
        $("#media-only").hide();
        is_gis_authorized = true;
    }

    /**
     * get_distance_meters: get the distance in meters between two [x,y] points
     * @param A first [x,y] point
     * @param B second [x,y] point
     * @return the distance in meters or undefined if no prejection is defined
     */
    this.get_distance_meter = (A, B) => {
        log.debug("View.get_distance_meters", {A, B});

        if (this.is_gis) {
            let distance = olDriver.get_distance(olDriver.to_lon_lat(olDriver.from_pixel_to_ol(A)), olDriver.to_lon_lat(olDriver.from_pixel_to_ol(B)));
            log.debug(`View.get_distance_meters return ${distance}`);
            return distance;
        } else {
            if (global.storage.selected_context.projection.project_invert != null) {
                // use D3
                let distance = d3.geoDistanceMeters(global.storage.selected_context.projection.project_invert(A), global.storage.selected_context.projection.project_invert(B));
                log.debug(`View.get_distance_meters return ${distance}`);
                return distance;
            }
        }
    };
};

global.app.bootstrap.register_module({
    name: "view",
    deps: ["dom_loaded", "default_configuration"],
    init: () => {
        global.view = new View("#map_area", {
            top: "#status_area",
            left: "#command_area_left",
            right: "#command_area_right"
        });
        global.view.resize();
    }
});

global.app.bootstrap.register_module({
    name: "view_tools",
    deps: ["tools", "view"],
    init: () => {
        global.tools.register({
            name: "geocoding",
            icon: "search_location",
            position: "#viewing_tools",
            classes: "",
            handler: async () => {
                global.view.ol_driver.search_address();
            }
        });
        global.tools.register({
            name: "recenter",
            icon: "expand",
            position: "#viewing_tools",
            classes: "",
            handler: () => {
                global.scene.map.center();
            }
        });
        global.tools.register({
            name: "zoom_plus",
            icon: "zoom_plus",
            position: "#viewing_tools",
            classes: "",
            handler: () => {
                global.view.zoom_by(1.1);
            }
        });
        global.tools.register({
            name: "zoom_minus",
            icon: "zoom_minus",
            position: "#viewing_tools",
            classes: "",
            handler: () => {
                global.view.zoom_by(0.9);
            }
        });
        global.tools.register({
            name: "map_type_assistant",
            icon: "plans",
            position: "#viewing_tools",
            classes: "",
            handler: async () => {
                if (global.view.is_gis) {
                    global.view.switch_map_basic_layer(true);
                }
            }
        });
        global.tools.tools["map_type_assistant"].$button.hide();
        global.tools.tools["geocoding"].$button.hide();
    }
});

export default View;
